import React, { useState } from 'react';
import { CheckCircle2, Eye, Clock, Video, ChevronDown, Users } from 'lucide-react';

/**
 * AslCheatsheet — collapsible reference list of ASL signs grouped by category.
 *
 * Each entry surfaces:
 *   • word + hint
 *   • detection_status badge:
 *       static / temporal → green "Detectable" pill
 *       catalog_only      → muted "Reference only" pill
 *   • motion mini-badge (only for temporal)
 *   • "Video coming soon" line when asl_video_url is null
 */

const CATEGORY_LABELS = {
  everyday_basics: 'Everyday Basics',
  food_ordering: 'Food / Ordering',
};

const STATUS_BADGE = {
  static:       { className: 'sv-badge-detect',   label: 'Detectable',     Icon: CheckCircle2 },
  temporal:     { className: 'sv-badge-detect',   label: 'Detectable',     Icon: CheckCircle2 },
  catalog_only: { className: 'sv-badge-ref',      label: 'Reference only', Icon: Eye },
};

function CheatRow({ entry }) {
  const statusKey = entry.detection_status || 'static';
  const badge = STATUS_BADGE[statusKey] || STATUS_BADGE.static;
  const isTemporal = statusKey === 'temporal';
  const isBimanual = entry.requires_two_hands === true;
  const noVideo = !entry.asl_video_url;

  return (
    <li
      className="sv-dict-item"
      data-testid={`dict-${entry.id}`}
      data-status={statusKey}
      data-two-handed={isBimanual ? 'true' : 'false'}
    >
      <div className="sv-dict-row-top">
        <span className="sv-dict-word">{entry.word}</span>
        <span className={`sv-mini-badge ${badge.className}`} data-testid={`status-${entry.id}`}>
          <badge.Icon className="w-3 h-3" strokeWidth={2.4} />
          <span>{badge.label}</span>
        </span>
      </div>
      <span className="sv-dict-hint">{entry.hint}</span>
      <div className="sv-dict-row-meta">
        {isBimanual && (
          <span className="sv-mini-badge sv-badge-two-handed" data-testid={`two-handed-${entry.id}`}>
            <Users className="w-3 h-3" strokeWidth={2.4} />
            <span>Two-handed</span>
          </span>
        )}
        {isTemporal && (
          <span className="sv-mini-badge sv-badge-motion" data-testid={`motion-${entry.id}`}>
            <Clock className="w-3 h-3" strokeWidth={2.4} />
            <span>Motion sign</span>
          </span>
        )}
        {noVideo && (
          <span className="sv-mini-badge sv-badge-video" data-testid={`video-${entry.id}`}>
            <Video className="w-3 h-3" strokeWidth={2.4} />
            <span>Video reference coming soon</span>
          </span>
        )}
      </div>
    </li>
  );
}

function CategorySection({ id, entries, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const label = CATEGORY_LABELS[id] || id;
  return (
    <section className="sv-cheat-section" data-testid={`cheat-section-${id}`} data-open={open}>
      <button
        type="button"
        className="sv-cheat-section-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`cheat-section-toggle-${id}`}
      >
        <span className="sv-eyebrow sv-cheat-section-label">{label}</span>
        <span className="sv-cheat-section-count">{entries.length}</span>
        <ChevronDown
          className={`w-4 h-4 sv-cheat-section-chev ${open ? 'is-open' : ''}`}
          strokeWidth={2.2}
        />
      </button>
      {open && (
        <ul className="sv-cheat-list">
          {entries.map((e) => <CheatRow key={e.id} entry={e} />)}
        </ul>
      )}
    </section>
  );
}

export default function AslCheatsheet({ entries, category = 'all' }) {
  if (!Array.isArray(entries) || entries.length === 0) return null;

  // Bucket each entry into EXACTLY ONE section to keep data-testids unique.
  //   • When filtered to a specific category → all entries go in that bucket.
  //   • In "All" view → each entry goes in its FIRST listed category (so a
  //     contextual entry like `thanks` or `more` renders once, not twice).
  const buckets = {};
  entries.forEach((e) => {
    const cats = e.categories?.length ? e.categories : ['everyday_basics'];
    const targetCat = (category && category !== 'all') ? category : cats[0];
    buckets[targetCat] = buckets[targetCat] || [];
    buckets[targetCat].push(e);
  });

  const orderedKeys = ['everyday_basics', 'food_ordering'].filter((k) => buckets[k]?.length);

  return (
    <div className="sv-card p-4 sv-cheatsheet" data-testid="cheatsheet">
      <p className="sv-eyebrow mb-3">ASL Dictionary · {entries.length} signs</p>
      <div className="sv-cheat-sections">
        {orderedKeys.map((k) => (
          <CategorySection key={k} id={k} entries={buckets[k]} />
        ))}
      </div>
    </div>
  );
}
